"use client";

import React, { useId } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation } from "swiper/modules";
import SavingsOfferCard, { SavingsOffer } from "./SavingsOfferCard";

import "swiper/css";
import "swiper/css/navigation";

/* Re-export the type so SavingsCoupons.tsx only needs one import */
export type { SavingsOffer };

interface Props {
  offers: SavingsOffer[];
}

const SavingsCouponsSlider = ({ offers }: Props) => {
  /* Unique class names prevent nav-button collisions when multiple
     instances of this slider appear on the same page */
  const uid = useId().replace(/:/g, "");
  const prevClass = `savings-prev-${uid}`;
  const nextClass = `savings-next-${uid}`;

  return (
    <div className="relative px-1">
      <Swiper
        modules={[Navigation]}
        spaceBetween={20}
        slidesPerView={1}
        navigation={{ prevEl: `.${prevClass}`, nextEl: `.${nextClass}` }}
        breakpoints={{
          640: { slidesPerView: 2, spaceBetween: 16 },
          1024: { slidesPerView: 3, spaceBetween: 20 },
        }}
        className="!pb-2"
      >
        {offers.map((offer, i) => (
          <SwiperSlide key={`${offer.uniqueId}-${i}`} className="!h-auto">
            <SavingsOfferCard offer={offer} />
          </SwiperSlide>
        ))}
      </Swiper>

      {/* ── Prev button ── */}
      <button
        className={`${prevClass} absolute left-0 top-1/2 -translate-y-1/2 -translate-x-5 z-10 w-10 h-10 rounded-full bg-white border border-[#E5E7EB] shadow-md flex items-center justify-center text-[#1E3A5F] hover:bg-[#1E3A5F] hover:text-white hover:border-[#1E3A5F] transition-all duration-200 disabled:opacity-30 disabled:cursor-not-allowed`}
        aria-label="Previous slide"
      >
        <svg
          className="w-4 h-4"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          aria-hidden="true"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2.5}
            d="M15 19l-7-7 7-7"
          />
        </svg>
      </button>

      {/* ── Next button ── */}
      <button
        className={`${nextClass} absolute right-0 top-1/2 -translate-y-1/2 translate-x-5 z-10 w-10 h-10 rounded-full bg-white border border-[#E5E7EB] shadow-md flex items-center justify-center text-[#1E3A5F] hover:bg-[#1E3A5F] hover:text-white hover:border-[#1E3A5F] transition-all duration-200 disabled:opacity-30 disabled:cursor-not-allowed`}
        aria-label="Next slide"
      >
        <svg
          className="w-4 h-4"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          aria-hidden="true"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2.5}
            d="M9 5l7 7-7 7"
          />
        </svg>
      </button>
    </div>
  );
};

export default SavingsCouponsSlider;
